import countries from "country-list";
import { Card, Col, Row, Form, Input, Divider, Modal, Button, Checkbox, message, Select, InputNumber } from "antd";
import { CheckCircleTwoTone } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { Elements as ElementsProvider, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import Styles from "./Signup.scss";
import Loading from "../../../components/Loading/Loading";
import { AppManager } from "../../../manager";
import BasicErrorView from "../../../components/ErrorView/BasicErrorView/BasicErrorView";
import { useForm } from "antd/lib/form/Form";
import { IAccountDetails, TAuthInfo } from "../../../models/Auth/Auth";

export const COUNTRIES = countries.getData().map((t) => ({ label: t.name, value: t.name }));

export interface CheckoutInterface {
    accountType: "dealer_group" | "planner";
}

const BillingItemView: React.FC<{
    name: string;
    price: string;
    label: string;
    onCheck?: (checked?: boolean) => void;
    checked?: boolean;
}> = ({ name, price, checked, label, onCheck }) => {
    return (
        <div className={Styles.BillingItemView}>
            <div className={Styles.ItemInfo}>
                <Checkbox checked={checked} disabled={!onCheck} onChange={(e) => onCheck?.(e.target.checked)} style={{ marginRight: 7 }} />
                <span className={Styles.Name}>{name}</span>
                <div className={Styles.Line} />
                <span className={Styles.Price}>{price}</span>
            </div>
            <span className={Styles.Label}>{label}</span>
        </div>
    );
};

const SuccessView: React.FC<{ email: string }> = ({ email }) => {
    return (
        <div style={{ marginTop: 20 }}>
            <div className={Styles.SuccessIconWrapper}>
                <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 150 }} />
            </div>
            <div style={{ textAlign: "center", marginTop: 20 }}>
                <h1 style={{ fontWeight: "bold" }}>New account request submitted!</h1>
                <h3>We have received your request to join Ausplan. Your submitted details are currently under review.</h3>
                <h3>
                    We shall let you know what to do next through your email once your account has been reviewed <b>{email}</b>.
                </h3>
            </div>
        </div>
    );
};

export const CheckoutContentMain: React.FC<{ product: any; accountType: CheckoutInterface["accountType"] }> = ({ accountType, product }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [form] = useForm();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmitForm = async (data: any) => {
        try {
            if (!data.tcAgreed) {
                return message.error("You'll need to agree to the terms and conditions");
            }
            if (typeof data.seats !== "number" || data.seats < 1 || data.seats % 1 !== 0) {
                return message.error("Invalid seats value!");
            }
            if (!elements || !stripe) throw new Error("Failed to properly initialize stripe. Please contact support if this persist");
            setLoading(true);
            const { error, setupIntent } = await stripe.confirmSetup({
                elements,
                redirect: "if_required",
            });
            if (error) throw error.message;
            const paymentMethodId = setupIntent.payment_method;

            const accountData: IAccountDetails = {
                ...data,
                productType: accountType,
                paymentDetails: {
                    paymentMethodId: paymentMethodId,
                },
            };
            AppManager.auth.createAccount(accountData, async (err, data) => {
                if (err) {
                    Modal.error({ title: "Error Creating Account", content: `${err}` });
                    setLoading(false);
                } else {
                    setSuccess(true);
                    setLoading(false);
                }
            });
        } catch (error) {
            Modal.error({ title: "Error", content: `${error}` });
            setLoading(false);
        }
    };

    const currency = product.default_price.currency;
    const unitPrice = product.default_price.unit_amount / 100;

    return (
        <div className="match-parent" style={{ position: "relative" }}>
            <div style={{ position: "absolute", overflowY: "auto", padding: "20px", top: 0, left: 0, bottom: 0, right: 0 }}>
                <Card style={{ minHeight: "100%" }}>
                    {success ? (
                        <SuccessView email={form.getFieldValue("email")} />
                    ) : (
                        <Form layout="vertical" form={form} onFinish={handleSubmitForm} initialValues={{ country: "Australia", seats: 1 }}>
                            <Row>
                                <Col md={24} lg={13} className={Styles.CheckoutFormContainer}>
                                    <h2 style={{ marginBottom: 20 }}>Create new account</h2>
                                    <div className={Styles.CardInfoWrapper}>
                                        <Form.Item label="First Name" name="firstName" rules={[{ required: true }]} hasFeedback requiredMark>
                                            <Input size="large" placeholder="Enter your first name" />
                                        </Form.Item>
                                        <Form.Item label="Last Name" name="lastName" rules={[{ required: true }]} hasFeedback requiredMark>
                                            <Input size="large" placeholder="Enter your last name" />
                                        </Form.Item>
                                        <Row gutter={10}>
                                            <Col md={12} sm={24}>
                                                <Form.Item
                                                    label="Email"
                                                    name="email"
                                                    rules={[{ required: true, type: "email" }]}
                                                    hasFeedback
                                                    requiredMark
                                                >
                                                    <Input size="large" placeholder="Enter your email" />
                                                </Form.Item>
                                            </Col>
                                            <Col md={12} sm={24}>
                                                <Form.Item label="Phone Number" name="phoneNumber">
                                                    <Input size="large" placeholder="Enter your phone number" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={10}>
                                            <Col md={12} sm={24}>
                                                <Form.Item label="Country" name="country" rules={[{ required: true }]} hasFeedback requiredMark>
                                                    <Select options={COUNTRIES} size="large" placeholder="Select your country" />
                                                </Form.Item>
                                            </Col>
                                            <Col md={12} sm={24}>
                                                <Form.Item label="Address" name="address" rules={[{ required: true }]} hasFeedback requiredMark>
                                                    <Input size="large" placeholder="Enter your address" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Form.Item
                                            label="Organization/Company Name"
                                            name="orgName"
                                            rules={[{ required: true }]}
                                            hasFeedback
                                            requiredMark
                                        >
                                            <Input size="large" placeholder="Enter your company name" />
                                        </Form.Item>
                                        <Form.Item label="ABN" name="abn" rules={[{ required: true }]} hasFeedback requiredMark>
                                            <Input size="large" placeholder="Enter your Australian Business Number" />
                                        </Form.Item>
                                        <Divider style={{ marginTop: 55 }}>Payment Information</Divider>
                                        <Form.Item label="" name="paymentDetails">
                                            <PaymentElement />
                                        </Form.Item>
                                        {/* <Form.Item label="Name on card" name="cardName" rules={[{ required: true }]} hasFeedback requiredMark>
                                            <Input size="large"/>
                                        </Form.Item> */}
                                    </div>
                                </Col>
                                <Col md={24} lg={11} className={Styles.SubscriptionInfoWrapper}>
                                    <h5 className={Styles.Subtitle}>SUBSCRIBING TO</h5>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <h2 className={Styles.SubscriptionName}>{product.name}</h2>
                                        <Button onClick={AppManager.route.gotoSignup}>Change</Button>
                                    </div>
                                    <Divider />
                                    <div className={Styles.SubscriptionContent}>
                                        <Form.Item name="tcAgreed" noStyle />
                                        <Form.Item noStyle shouldUpdate>
                                            {({ getFieldValue, setFieldsValue }) => (
                                                <div>
                                                    <BillingItemView
                                                        name="Subscription"
                                                        price={`${currency} ${unitPrice}`}
                                                        label={`Per seat, per ${product.default_price.recurring.interval}`}
                                                        checked
                                                    />
                                                    <BillingItemView
                                                        name="Migration from xplan"
                                                        price={"free"}
                                                        label=""
                                                        checked
                                                    // onCheck={(val) => setFieldsValue({ migrateXplan: val })}
                                                    />
                                                    <Form.Item label="Number of seats" name="seats">
                                                        <InputNumber style={{ width: "100%" }} min={1} size="large" />
                                                    </Form.Item>
                                                    <Form.Item label="" name="discountCode">
                                                        <Input addonBefore="Discount code" placeholder="Enter your discount code" size="large" />
                                                    </Form.Item>
                                                </div>
                                            )}
                                        </Form.Item>
                                    </div>
                                    <Divider />
                                    <Form.Item shouldUpdate>
                                        {({ getFieldValue, setFieldsValue, getFieldError }) => (
                                            <div className={Styles.TotalView}>
                                                <span className={Styles.Total}>Total</span>
                                                <span className={Styles.Amount}>
                                                    {currency} {getFieldValue("seats") * unitPrice}
                                                </span>
                                            </div>
                                        )}
                                    </Form.Item>
                                    <Form.Item shouldUpdate>
                                        {({ getFieldValue, setFieldsValue, getFieldError }) => (
                                            <>
                                                <div className={Styles.TCAgreementView}>
                                                    <Checkbox
                                                        checked={getFieldValue("tcAgreed")}
                                                        onChange={(e) => setFieldsValue({ tcAgreed: e.target.checked })}
                                                        style={{ marginRight: 7 }}
                                                    />
                                                    By creating a new account, I agree to the <a>Terms and conditions</a> for the usage of Ausplan.
                                                </div>
                                                {getFieldError("tcAgreed") ? <span style={{ color: "red" }}>{getFieldError("tcAgreed")}</span> : null}
                                            </>
                                        )}
                                    </Form.Item>
                                    <div style={{ textAlign: "center", paddingTop: 10 }}>
                                        <Button
                                            size="large"
                                            type="primary"
                                            style={{ width: "100%" }}
                                            htmlType="submit"
                                            loading={loading}
                                            disabled={loading}
                                        >
                                            Submit
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    )}
                </Card>
            </div>
        </div>
    );
};

export const CheckoutContent: React.FC<CheckoutInterface> = ({ accountType }) => {
    const [stripePromise, setStripePromise] = useState<Promise<Stripe | null>>();
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string>();
    const [product, setProduct] = useState<any>();
    const [authInfo, setAuthInfo] = useState<TAuthInfo>();

    const loadSignupInfo = () => {
        setLoading(true);
        setLoadError(undefined);
        console.log("LOADING...");
        AppManager.auth.fetchAuthInfo((err, data) => {
            setLoading(false);
            if (err) setLoadError(`${err}`);
            else {
                setAuthInfo(data);
                const sp = loadStripe(data!.stripePublicKey);
                setStripePromise(sp);
                setProduct(
                    data!.products.find(
                        (r) =>
                            r.id == (accountType == "planner" ? data?.plannerProdId : accountType == "dealer_group" ? data?.dealerGroupProdId : ""),
                    ),
                );
                // console.log("KEY ", awaisp);
            }
        }, true);
    };

    useEffect(() => {
        loadSignupInfo();
    }, []);
    return loading ? (
        <Loading />
    ) : loadError ? (
        <Card style={{ margin: 20 }}>
            <div style={{ display: "flex" }}>
                <BasicErrorView error="Failed to initialize page" description={`${loadError}`} />
                <Button onClick={loadSignupInfo}>Retry</Button>
            </div>
        </Card>
    ) : stripePromise && product && authInfo ? (
        <ElementsProvider stripe={stripePromise} options={{ clientSecret: authInfo.stripeSetupIntentSecret }}>
            <CheckoutContentMain product={product} accountType={accountType} />
        </ElementsProvider>
    ) : null;
};
